import { Currency } from 'dinero.js';
import { computed, ComputedRef} from 'vue';
import { merchantInterface, MerchantUserRole } from '@/api/merchants';
import { serviceProviderInterface, ServiceProviderUserRole, ServiceProviderPermissionEnum} from '@/api/serviceProviders';
import { userInterface, UserUuid} from '@/api/users';
import { useCurrentUserStore } from '@/stores/currentUser';
import { useMerchantsStore } from '@/stores/merchants';
import { useNarrativeStore } from '@/stores/narrative';
import { AnalyticsKeys } from './useAnalytics';

export const useNarrative = (): { env: ComputedRef<string>; appName: ComputedRef<string>; appBaseDomain: ComputedRef<string>; stripeKey: ComputedRef<string>; currentServiceProvider: ComputedRef<serviceProviderInterface>; merchants: ComputedRef<merchantInterface[]>; currentMerchant: ComputedRef<merchantInterface>; currentUser: ComputedRef<userInterface>; currentServiceProviderOptional: ComputedRef<{ id: number; name: string; subdomainName: string; domainName: string; platformName: string; platformKey: number; platformDomain: string; logoFile: string; daysOfFreeStorage: number; defaultAutomaticClientBilling: boolean; supportEmail: string; faviconFile: string; stripeAccountId: string; logoBackground: string; currency: Currency; availablePermissions: ServiceProviderPermissionEnum[]; preferences: { inventory: { products: { traceabilityGroups: { enabled: boolean; }; }; }; clientSidebar: { links: { name: string; url: string; }[]; }; }; shippingSettings: { pick: { locationScan: 'required' | 'optional' | 'disabled'; itemScan: 'required' | 'required_first_pick' | 'optional' | 'disabled'; }; }; viewSettings: { metric?: { enabled: boolean; } | undefined; shipments?: { addItems?: { showExpiryDate?: { enabled: boolean; minimumDays?: number | undefined; } | undefined; } | undefined; createInboundShipmentDefault?: { enabled: boolean; } | undefined; } | undefined; items?: { showCondition: { enabled: boolean; }; } | undefined; sounds?: { enabled: boolean; } | undefined; theme?: string | undefined; }; } | undefined>; currentMerchantOptional: ComputedRef<merchantInterface | undefined>; currentUserOptional: ComputedRef<{ id: number; uuid: UserUuid; createdAt: string; name: string; email: string; photoUrl: string; phone: string; onboarding?: { accountSettingsConfigured?: number | 'complete' | 'cancelled' | undefined; warehouseConfigured?: number | 'complete' | 'cancelled' | undefined; servicesConfigured?: number | 'complete' | 'cancelled' | undefined; clientUser?: number | 'complete' | 'cancelled' | undefined; shipmentIndex?: number | 'complete' | 'cancelled' | undefined; inboundShipment?: number | 'complete' | 'cancelled' | undefined; outboundShipment?: number | 'complete' | 'cancelled' | undefined; inventory?: number | 'complete' | 'cancelled' | undefined; billing?: number | 'complete' | 'cancelled' | undefined; } | undefined; } | undefined>; userHasRoleOnCurrentServiceProvider: ComputedRef<boolean>; userHasRoleOnCurrentMerchant: ComputedRef<boolean>; userIsOwnerOnServiceProvider: ComputedRef<boolean>; userIsOfficeEmployeeOnServiceProvider: ComputedRef<boolean>; userIsOwnerOnMerchant: ComputedRef<boolean>; userIsDeveloper: ComputedRef<boolean>; impersonatingUserId: ComputedRef<number | null>; sentryPublicDsn: ComputedRef<string | undefined>; releaseTag: ComputedRef<string | undefined>; analyticsKeys: ComputedRef<AnalyticsKeys>; featurebaseUserIdHash: ComputedRef<string | null>; featurebaseServiceProviderIdHash: ComputedRef<string | null>; featurebaseOrganization: ComputedRef<string | null>; showMerchantOnIndex: ComputedRef<boolean>; clearCurrentMerchant: () => void, setCurrentMerchantFromId: (merchantId: number) => Promise<merchantInterface | undefined>; getMerchantFromId: (merchantId: number) => merchantInterface | undefined; refreshServiceProvider: () => Promise<void>; refreshMerchant: () => Promise<void>; logoUrl: ComputedRef<string>; faviconUrl: ComputedRef<string>; } => {
    const narrativeStore = useNarrativeStore();
    const merchantsStore = useMerchantsStore();
    const currentUserStore = useCurrentUserStore();

    const setCurrentMerchantFromId = async (merchantId: number): Promise<merchantInterface | undefined> => {
        if (!narrativeStore.currentServiceProvider) {
            return undefined;
        }

        let merchant = merchantsStore.merchants.find(merchant => merchant.id === merchantId);

        // Staff can open shipments/orders for merchants that are absent from the loaded
        // list (e.g. disabled merchants), so fetch the merchant on demand to establish
        // full merchant context instead of leaving it unset and throwing downstream.
        if (merchant === undefined) {
            merchant = await merchantsStore.fetchMerchantById(merchantId);
        }

        if (merchant !== undefined) {
            narrativeStore.setCurrentMerchant(merchant);
        }

        return merchant;
    };

    const showMerchantOnIndex = computed(() => {
        if (narrativeStore.currentMerchant) {
            return false;
        }

        return true;
    });

    const getMerchantFromId = (merchantId: number): merchantInterface | undefined => {
        return merchantsStore.merchants.find(merchant => merchant.id === merchantId);
    };

    const logoUrl = computed(() => {
        if (narrativeStore.currentServiceProvider?.logoFile == null || narrativeStore.currentServiceProvider.logoFile === '') {
            return '';
        }

        if (narrativeStore.currentServiceProvider.logoFile.includes('http')) {
            return narrativeStore.currentServiceProvider.logoFile;
        }

        return `/storage/${narrativeStore.currentServiceProvider.logoFile}`;
    });

    const faviconUrl = computed(() => {
        if (narrativeStore.currentServiceProvider?.faviconFile == null || narrativeStore.currentServiceProvider.faviconFile === '') {
            return '';
        }

        if (narrativeStore.currentServiceProvider.faviconFile.includes('http')) {
            return narrativeStore.currentServiceProvider.faviconFile;
        }

        return `/storage/${narrativeStore.currentServiceProvider.faviconFile}`;
    });

    return {
        env: computed((): string => narrativeStore.env),
        appName: computed((): string => narrativeStore.appName),
        appBaseDomain: computed((): string => narrativeStore.appBaseDomain),
        stripeKey: computed((): string => narrativeStore.stripeKey),
        currentServiceProvider: computed((): serviceProviderInterface => {
            const serviceProvider = narrativeStore.currentServiceProvider;
            if (!serviceProvider) throw new Error('Service provider is required');

            return serviceProvider;
        }),
        merchants: computed((): merchantInterface[] => merchantsStore.merchants),
        currentMerchant: computed((): merchantInterface => {
            const currentMerchant = narrativeStore.currentMerchant;
            if (!currentMerchant) throw new Error('Merchant is required');

            return currentMerchant;
        }),
        currentUser: computed((): userInterface => {
            const user = currentUserStore.currentUser;
            if (!user) throw new Error('User is required');

            return user;
        }),
        currentServiceProviderOptional: computed(() => narrativeStore.currentServiceProvider),
        currentMerchantOptional: computed(() => narrativeStore.currentMerchant),
        currentUserOptional: computed(() => currentUserStore.currentUser),
        userHasRoleOnCurrentServiceProvider: computed((): boolean => currentUserStore.userHasRoleOnCurrentServiceProvider()),
        userHasRoleOnCurrentMerchant: computed((): boolean => currentUserStore.userHasRoleOnCurrentMerchant()),
        userIsOwnerOnServiceProvider: computed((): boolean => currentUserStore.userHasRoleOnCurrentServiceProvider(ServiceProviderUserRole.Owner)),
        userIsOfficeEmployeeOnServiceProvider: computed((): boolean => currentUserStore.userHasRoleOnCurrentServiceProvider(ServiceProviderUserRole.OfficeEmployee)),
        userIsOwnerOnMerchant: computed((): boolean => currentUserStore.userHasRoleOnCurrentMerchant(MerchantUserRole.Owner)),
        userIsDeveloper: computed((): boolean => currentUserStore.userIsDeveloper()),
        impersonatingUserId: computed((): number | null => narrativeStore.impersonatingUserId),
        sentryPublicDsn: computed((): string | undefined => narrativeStore.sentryPublicDsn),
        releaseTag: computed((): string | undefined => narrativeStore.releaseTag),
        analyticsKeys: computed((): AnalyticsKeys => narrativeStore.analyticsKeys),
        featurebaseUserIdHash: computed((): string | null => narrativeStore.featurebaseUserIdHash),
        featurebaseServiceProviderIdHash: computed((): string | null => narrativeStore.featurebaseServiceProviderIdHash),
        featurebaseOrganization: computed((): string | null => narrativeStore.featurebaseOrganization),
        showMerchantOnIndex,
        clearCurrentMerchant: narrativeStore.clearCurrentMerchant,
        setCurrentMerchantFromId,
        getMerchantFromId,
        refreshServiceProvider: narrativeStore.refreshServiceProvider,
        refreshMerchant: merchantsStore.refreshMerchant,
        logoUrl,
        faviconUrl,
    };
};
